/**
 * Regras de alarme: lista, editor guiado, simulação e versões — Nexora Grid (frontend, US-2101).
 *
 * Criado em: 19/10/2026 · Última modificação: 25/10/2026 · Versão: 1.1.0
 *
 * O motor e a API já existiam (ADR-0009, US-1704); esta é a tela. Todos que veem a operadora consultam e simulam;
 * engenharia, supervisor e a equipe Nexora criam, alteram, ativam e desativam (matriz: `editar_regras`). Os limites
 * são do cliente (P-014): os modelos prontos (NBR 12218, reservatório, motor trifásico) são só o ponto de partida.
 * Regra nova nasce **desativada**: simule nas últimas 24 h e ative. Ações de manobra não são editadas aqui.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import { quando } from "../admin/tipos";
import { MEDIDAS, nomeMedida, unidadeMedida } from "../cco/medidas";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";

type Condicao = { medida: string; operador: string; limite: number; duracao_s: number; histerese: number };
type Acao = { tipo: string; mensagem: string; severidade?: string | null; tipo_alarme?: string | null; canais?: string[] };
type Definicao = { nome: string; descricao?: string; escopo: { nivel: "dispositivo" | "unidade" | "tipo_equipamento"; valor: string };
  gatilho: string; estagio: string; condicoes: Condicao[]; combinacao: "todas" | "qualquer"; acoes: Acao[]; prioridade: number;
  intervalo_s?: number | null };
type Regra = { id: string; empresa: string; ativa: boolean; versao: number; definicao: Definicao; atualizado_em: string;
  disparos_7d?: number; previa?: string };
type Modelo = { id: string; grupo: string; referencia: string; definicao: Definicao; previa: string };
type Simulacao = { aplicavel: boolean; motivo?: string; disparos: number; dispositivos_avaliados: number; leituras: number; horas: number;
  exemplos: { instante: string; device_id: string; valores: Record<string, number> }[] };
type Versao = { versao: number; autor: string; motivo: string; criado_em: string; definicao: Definicao };

const NIVEL: Record<Definicao["escopo"]["nivel"], string> = { dispositivo: "Dispositivo", unidade: "Setor / unidade", tipo_equipamento: "Tipo de equipamento" };
const OPERADORES = ["<", "<=", ">", ">=", "==", "!="];
// medidas da lista do CCO + as calculadas pelo motor (não ficam no hub)
const MEDIDAS_REGRA = [...Object.keys(MEDIDAS), "corrente_pct_nominal"];
const rotulo = (m: string) => (m === "corrente_pct_nominal" ? "Corrente (% da nominal, calculada pelo motor)"
  : m === "*" ? "qualquer medida (sem comunicação, US-3007)" : nomeMedida(m));
const VAZIA: Definicao = { nome: "", escopo: { nivel: "tipo_equipamento", valor: "" }, gatilho: "payload", estagio: "alerta",
  condicoes: [{ medida: "pressao_rede_mca", operador: "<", limite: 10, duracao_s: 600, histerese: 1 }], combinacao: "todas",
  acoes: [{ tipo: "gerar_alarme", mensagem: "", severidade: "ALERTA", tipo_alarme: "" }], prioridade: 50 };

/** Mesma prévia do backend (`regras.simulacao.descrever`), com os nomes da tela. */
export function previa(d: Definicao): string {
  const conds = d.condicoes.map((c) => {
    let t = `${rotulo(c.medida)} ${c.operador} ${c.limite}${unidadeMedida(c.medida) ? ` ${unidadeMedida(c.medida)}` : ""}`;
    if (c.duracao_s) t += c.duracao_s >= 60 ? ` por ${Math.floor(c.duracao_s / 60)} min` : ` por ${c.duracao_s} s`;
    if (c.histerese) t += ` (histerese ${c.histerese})`;
    return t;
  }).join(d.combinacao === "todas" ? " e " : " ou ");
  const onde = { dispositivo: "no dispositivo", unidade: "no setor/unidade", tipo_equipamento: "em todo equipamento do tipo" }[d.escopo.nivel];
  const acoes = d.acoes.map((a) => (a.tipo === "gerar_alarme" ? `alarme ${a.severidade} ${a.tipo_alarme}` : a.tipo === "notificar" ? `notificar (${(a.canais ?? []).join(", ")})` : a.tipo.replace(/_/g, " "))).join(", ");
  return `${conds} ${onde} ${d.escopo.valor || "…"} → ${acoes}`;
}

function useOpcoesEscopo(empresa: string | null) {
  const [o, setO] = useState<{ dispositivo: string[]; unidade: string[]; tipo_equipamento: string[] }>({ dispositivo: [], unidade: [], tipo_equipamento: [] });
  useEffect(() => {
    if (!empresa) return;
    Promise.all([
      api<{ device_id: string; tipo: string; unidade: string }[]>(`/v1/admin/dispositivos?empresa=${empresa}`).catch(() => []),
      api<{ setores: { codigo: string }[]; nao_cadastrados: { codigo: string }[] }>(`/v1/admin/empresas/${empresa}/setores`).catch(() => ({ setores: [], nao_cadastrados: [] })),
    ]).then(([ds, st]) => setO({
      dispositivo: ds.map((d) => d.device_id).sort(),
      unidade: [...new Set([...st.setores.map((s) => s.codigo), ...st.nao_cadastrados.map((s) => s.codigo), ...ds.map((d) => d.unidade)])].sort(),
      tipo_equipamento: [...new Set(["bomba", "reservatorio", "poco", "poco_profundo", "ponto_pressao", ...ds.map((d) => d.tipo).filter(Boolean)])].sort(),
    }));
  }, [empresa]);
  return o;
}

export function Regras() {
  const { usuario, empresa } = useSessao();
  const [lista, setLista] = useState<Regra[] | null>(null);
  const [filtro, setFiltro] = useState({ nivel: "", medida: "", severidade: "", ativa: "" });
  useEffect(() => { if (empresa) api<Regra[]>(`/v1/regras?empresa=${empresa}`).then(setLista).catch(() => setLista([])); }, [empresa]);
  if (!usuario) return null;
  const visiveis = (lista ?? []).filter((r) => (!filtro.nivel || r.definicao.escopo.nivel === filtro.nivel)
    && (!filtro.medida || r.definicao.condicoes.some((c) => c.medida === filtro.medida))
    && (!filtro.severidade || r.definicao.acoes.some((a) => a.severidade === filtro.severidade))
    && (!filtro.ativa || String(r.ativa) === filtro.ativa));
  const medidas = [...new Set((lista ?? []).flatMap((r) => r.definicao.condicoes.map((c) => c.medida)))].sort();
  return (
    <section className="admin">
      <header className="linha-titulo">
        <h2 className="nx-titulo">{NOMES.regras}</h2>
        <span className="espaco" />
        {pode.editarRegras(usuario) && <Link className="nx-botao" to="/regras/nova">Nova regra</Link>}
      </header>
      <div className="filtros">
        <div className="nx-campo"><label htmlFor="rf-escopo">Escopo</label>
          <select id="rf-escopo" value={filtro.nivel} onChange={(e) => setFiltro({ ...filtro, nivel: e.target.value })}>
            <option value="">todos</option>{Object.entries(NIVEL).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
        <div className="nx-campo"><label htmlFor="rf-medida">Medida</label>
          <select id="rf-medida" value={filtro.medida} onChange={(e) => setFiltro({ ...filtro, medida: e.target.value })}>
            <option value="">todas</option>{medidas.map((m) => <option key={m} value={m}>{rotulo(m)}</option>)}</select></div>
        <div className="nx-campo"><label htmlFor="rf-sev">Severidade</label>
          <select id="rf-sev" value={filtro.severidade} onChange={(e) => setFiltro({ ...filtro, severidade: e.target.value })}>
            <option value="">todas</option><option value="ALERTA">Alerta</option><option value="ALTO">Alto</option><option value="CRITICO">Crítico</option></select></div>
        <div className="nx-campo"><label htmlFor="rf-ativa">Situação</label>
          <select id="rf-ativa" value={filtro.ativa} onChange={(e) => setFiltro({ ...filtro, ativa: e.target.value })}>
            <option value="">todas</option><option value="true">Ativas</option><option value="false">Desativadas</option></select></div>
      </div>
      {!lista && <p className="nx-suave">carregando…</p>}
      {lista && (
        <div className="tabela-rolagem">
          <table className="tabela tabela-usuarios tabela-regras">
            <thead><tr><th>Regra</th><th>O que faz</th><th>Situação</th><th>Disparos (7 dias)</th><th>Versão</th></tr></thead>
            <tbody>
              {visiveis.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/regras/${r.id}`}>{r.definicao.nome}</Link></td>
                  <td><small>{previa(r.definicao)}</small></td>
                  <td><span className={`nx-selo ${r.ativa ? "situacao-validado" : "situacao-pendente"}`}>{r.ativa ? "Ativa" : "Desativada"}</span></td>
                  <td>{r.disparos_7d ?? 0}</td><td>{r.versao}</td>
                </tr>
              ))}
              {!visiveis.length && <tr><td colSpan={5} className="nx-suave">nenhuma regra</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ResultadoSimulacao({ s }: { s: Simulacao }) {
  if (!s.aplicavel) return <p className="nx-suave" role="status">{s.motivo}</p>;
  return (
    <div className="nx-cartao aviso-admin" role="status">
      <b>{s.disparos ? `Teria disparado ${s.disparos} vez(es)` : "Não teria disparado"}</b> nas últimas {s.horas} h
      {" "}({s.dispositivos_avaliados} dispositivo(s), {s.leituras} leitura(s)).
      {!s.leituras && <> Nenhuma leitura dessa medida no escopo: confira o escopo (ou a medida é calculada pelo motor e não fica no histórico).</>}
      {s.exemplos.length > 0 && <ul>{s.exemplos.slice(0, 5).map((x, i) => <li key={i}>{quando(x.instante)} · {x.device_id} · {Object.entries(x.valores).map(([m, v]) => `${rotulo(m)} ${v}`).join(", ")}</li>)}</ul>}
    </div>
  );
}

function Editor({ empresa, inicial, aoSalvar, aoCancelar, existente }: { empresa: string; inicial: Definicao; existente: boolean;
  aoSalvar: (d: Definicao, motivo: string) => Promise<void>; aoCancelar: () => void }) {
  const [d, setD] = useState<Definicao>(inicial);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [simulacao, setSimulacao] = useState<Simulacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const opcoes = useOpcoesEscopo(empresa);
  useEffect(() => { if (!existente) api<Modelo[]>("/v1/regras/modelos").then(setModelos).catch(() => setModelos([])); }, [existente]);
  const cond = (i: number, campo: Partial<Condicao>) => setD({ ...d, condicoes: d.condicoes.map((c, j) => (j === i ? { ...c, ...campo } : c)) });
  const acao = (i: number, campo: Partial<Acao>) => setD({ ...d, acoes: d.acoes.map((a, j) => (j === i ? { ...a, ...campo } : a)) });
  const simular = async () => {
    setErro(null);
    try { setSimulacao(await api<Simulacao>("/v1/regras/simular", { corpo: { empresa, definicao: d } })); }
    catch (x) { setErro(x instanceof ErroApi ? x.message : "Falha de rede."); }
  };
  const salvar = async () => {
    setErro(null);
    try { await aoSalvar(d, motivo); } catch (x) { setErro(x instanceof ErroApi ? x.message : "Falha de rede."); }
  };
  return (
    <div className="editor-regra nx-cartao">
      {!existente && modelos.length > 0 && (
        <div className="nx-campo"><label htmlFor="re-modelo">Começar de um modelo</label>
          <select id="re-modelo" defaultValue="" onChange={(e) => { const m = modelos.find((x) => x.id === e.target.value); if (m) { setD({ ...m.definicao }); setSimulacao(null); } }}>
            <option value="">— regra em branco —</option>
            {modelos.map((m) => <option key={m.id} value={m.id}>{m.grupo}: {m.definicao.nome} ({m.referencia})</option>)}
          </select><small className="nx-suave">Os limites são da operadora (seguindo as normas): ajuste antes de salvar.</small></div>
      )}
      <div className="grade-ficha">
        <div className="nx-campo"><label htmlFor="re-nome">Nome da regra</label>
          <input id="re-nome" required minLength={3} maxLength={100} value={d.nome} onChange={(e) => setD({ ...d, nome: e.target.value })} /></div>
        <div className="nx-campo"><label htmlFor="re-nivel">Aplicar a</label>
          <select id="re-nivel" value={d.escopo.nivel} onChange={(e) => setD({ ...d, escopo: { nivel: e.target.value as Definicao["escopo"]["nivel"], valor: "" } })}>
            {Object.entries(NIVEL).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
        <div className="nx-campo"><label htmlFor="re-valor">{NIVEL[d.escopo.nivel]}</label>
          <select id="re-valor" value={d.escopo.valor} onChange={(e) => setD({ ...d, escopo: { ...d.escopo, valor: e.target.value } })}>
            <option value="">—</option>
            {[...new Set([...(d.escopo.valor ? [d.escopo.valor] : []), ...opcoes[d.escopo.nivel]])].map((v) => <option key={v} value={v}>{v}</option>)}
          </select></div>
      </div>
      <h4>Condições</h4>
      {d.condicoes.map((c, i) => (
        <div key={i} className="linha-condicao">
          <div className="nx-campo"><label htmlFor={`re-medida-${i}`}>Medida</label>
            <select id={`re-medida-${i}`} value={c.medida} onChange={(e) => cond(i, { medida: e.target.value })}>
              {MEDIDAS_REGRA.map((m) => <option key={m} value={m}>{rotulo(m)}</option>)}</select></div>
          <div className="nx-campo"><label htmlFor={`re-op-${i}`}>Operador</label>
            <select id={`re-op-${i}`} value={c.operador} onChange={(e) => cond(i, { operador: e.target.value, histerese: [">", ">=", "<", "<="].includes(e.target.value) ? c.histerese : 0 })}>
              {OPERADORES.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
          <div className="nx-campo"><label htmlFor={`re-limite-${i}`}>Limite{unidadeMedida(c.medida) ? ` (${unidadeMedida(c.medida)})` : ""}</label>
            <input id={`re-limite-${i}`} type="number" step="any" value={c.limite} onChange={(e) => cond(i, { limite: Number(e.target.value) })} /></div>
          <div className="nx-campo"><label htmlFor={`re-dur-${i}`}>Por (min)</label>
            <input id={`re-dur-${i}`} type="number" min={0} max={1440} value={c.duracao_s / 60} onChange={(e) => cond(i, { duracao_s: Math.round(Number(e.target.value) * 60) })} /></div>
          <div className="nx-campo"><label htmlFor={`re-hist-${i}`}>Histerese</label>
            <input id={`re-hist-${i}`} type="number" min={0} step="any" value={c.histerese} onChange={(e) => cond(i, { histerese: Number(e.target.value) })} /></div>
          {d.condicoes.length > 1 && <button type="button" className="nx-botao" onClick={() => setD({ ...d, condicoes: d.condicoes.filter((_, j) => j !== i) })}>Remover</button>}
        </div>
      ))}
      <div className="acoes-conta">
        {d.condicoes.length < 10 && <button type="button" className="nx-botao" onClick={() => setD({ ...d, condicoes: [...d.condicoes, { ...VAZIA.condicoes[0] }] })}>+ condição</button>}
        {d.condicoes.length > 1 && <div className="nx-campo"><label htmlFor="re-comb">Disparar quando</label>
          <select id="re-comb" value={d.combinacao} onChange={(e) => setD({ ...d, combinacao: e.target.value as Definicao["combinacao"] })}>
            <option value="todas">todas as condições valerem</option><option value="qualquer">qualquer condição valer</option></select></div>}
      </div>
      <h4>Ações</h4>
      {d.acoes.map((a, i) => (
        <div key={i} className="linha-condicao">
          <div className="nx-campo"><label htmlFor={`re-acao-${i}`}>Ação</label>
            <select id={`re-acao-${i}`} value={a.tipo} disabled={!["gerar_alarme", "notificar"].includes(a.tipo)}
              onChange={(e) => acao(i, e.target.value === "notificar" ? { tipo: "notificar", canais: ["email"], severidade: null, tipo_alarme: null } : { tipo: "gerar_alarme", severidade: "ALERTA", tipo_alarme: "", canais: [] })}>
              <option value="gerar_alarme">Gerar alarme</option><option value="notificar">Notificar</option>
              {!["gerar_alarme", "notificar"].includes(a.tipo) && <option value={a.tipo}>{a.tipo} (não editável aqui)</option>}
            </select></div>
          {a.tipo === "gerar_alarme" && <>
            <div className="nx-campo"><label htmlFor={`re-sev-${i}`}>Severidade</label>
              <select id={`re-sev-${i}`} value={a.severidade ?? "ALERTA"} onChange={(e) => acao(i, { severidade: e.target.value })}>
                <option value="ALERTA">Alerta</option><option value="ALTO">Alto</option><option value="CRITICO">Crítico</option></select></div>
            <div className="nx-campo"><label htmlFor={`re-tipo-${i}`}>Tipo do alarme</label>
              <input id={`re-tipo-${i}`} value={a.tipo_alarme ?? ""} placeholder="ex.: PRESSAO_BAIXA" onChange={(e) => acao(i, { tipo_alarme: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })} /></div>
          </>}
          {a.tipo === "notificar" && ["email", "webhook", "whatsapp"].map((c) => (
            <label key={c} className="nx-suave"><input type="checkbox" checked={(a.canais ?? []).includes(c)}
              onChange={(e) => acao(i, { canais: e.target.checked ? [...(a.canais ?? []), c] : (a.canais ?? []).filter((x) => x !== c) })} /> {c}</label>
          ))}
          <div className="nx-campo campo-largo"><label htmlFor={`re-msg-${i}`}>Mensagem</label>
            <input id={`re-msg-${i}`} maxLength={200} value={a.mensagem} onChange={(e) => acao(i, { mensagem: e.target.value })} /></div>
          {d.acoes.length > 1 && <button type="button" className="nx-botao" onClick={() => setD({ ...d, acoes: d.acoes.filter((_, j) => j !== i) })}>Remover</button>}
        </div>
      ))}
      {d.acoes.length < 5 && <button type="button" className="nx-botao" onClick={() => setD({ ...d, acoes: [...d.acoes, { tipo: "notificar", mensagem: "", canais: ["email"] }] })}>+ ação</button>}
      <p className="previa-regra" aria-label="Prévia da regra"><b>Prévia:</b> {previa(d)}</p>
      <div className="acoes-conta">
        <button type="button" className="nx-botao" onClick={() => void simular()}>Simular nas últimas 24 h</button>
        {existente && <div className="nx-campo"><label htmlFor="re-motivo">Motivo da alteração</label>
          <input id="re-motivo" minLength={3} maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></div>}
        <button type="button" className="nx-botao nx-botao-principal" disabled={existente && motivo.trim().length < 3} onClick={() => void salvar()}>
          {existente ? "Salvar nova versão" : "Salvar (desativada)"}</button>
        <button type="button" className="nx-botao" onClick={aoCancelar}>Cancelar</button>
      </div>
      {simulacao && <ResultadoSimulacao s={simulacao} />}
      {erro && <p className="erro" role="alert">{erro}</p>}
    </div>
  );
}

export function RegraNova() {
  const { usuario, empresa } = useSessao();
  const navegar = useNavigate();
  if (!usuario || !empresa) return null;
  if (!pode.editarRegras(usuario)) return <p className="nx-suave">Sem permissão para criar regras.</p>;
  return (
    <section className="admin">
      <header className="linha-titulo"><h2 className="nx-titulo"><Link to="/regras">{NOMES.regras}</Link> › Nova regra</h2></header>
      <Editor empresa={empresa} inicial={VAZIA} existente={false} aoCancelar={() => navegar("/regras")}
        aoSalvar={async (d) => { const r = await api<Regra>("/v1/regras", { corpo: { empresa, definicao: d, ativa: false } }); navegar(`/regras/${r.id}`); }} />
    </section>
  );
}

/** Diferenças campo a campo entre duas versões (para "comparar"). */
function diferencas(a: unknown, b: unknown, caminho = ""): [string, unknown, unknown][] {
  if (typeof a === "object" && a !== null && typeof b === "object" && b !== null) {
    const chaves = new Set([...Object.keys(a as object), ...Object.keys(b as object)]);
    return [...chaves].flatMap((k) => diferencas((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k], caminho ? `${caminho}.${k}` : k));
  }
  return JSON.stringify(a) === JSON.stringify(b) ? [] : [[caminho, a, b]];
}

export function RegraDetalhe() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [r, setR] = useState<Regra | null>(null);
  const [versoes, setVersoes] = useState<Versao[]>([]);
  const [editando, setEditando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [simulacao, setSimulacao] = useState<Simulacao | null>(null);
  const [comparar, setComparar] = useState<[number, number] | null>(null);
  const carregar = useCallback(() => {
    api<Regra>(`/v1/regras/${id}`).then(setR).catch(() => setAviso("Regra não encontrada."));
    api<Versao[]>(`/v1/regras/${id}/versoes`).then(setVersoes).catch(() => setVersoes([]));
  }, [id]);
  useEffect(carregar, [carregar]);
  const diff = useMemo(() => {
    if (!comparar) return [];
    const [x, y] = comparar.map((v) => versoes.find((w) => w.versao === v)?.definicao);
    return x && y ? diferencas(x, y) : [];
  }, [comparar, versoes]);
  if (!usuario) return null;
  if (!r) return <p className="nx-suave">{aviso ?? "carregando…"}</p>;
  const edita = pode.editarRegras(usuario);
  const situacao = async (acao: "ativar" | "desativar") => {
    try { await api(`/v1/regras/${r.id}/${acao}`, { corpo: { motivo } }); setAviso(acao === "ativar" ? "Regra ativa: o motor passa a avaliá-la em até 30 s." : "Regra desativada."); setMotivo(""); carregar(); }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };
  const simular = async () => setSimulacao(await api<Simulacao>("/v1/regras/simular", { corpo: { empresa: r.empresa, definicao: r.definicao } }));
  return (
    <section className="admin">
      <header className="linha-titulo">
        <h2 className="nx-titulo"><Link to="/regras">{NOMES.regras}</Link> › {r.definicao.nome}</h2>
        <span className={`nx-selo ${r.ativa ? "situacao-validado" : "situacao-pendente"}`}>{r.ativa ? "Ativa" : "Desativada"}</span>
        <span className="espaco" />
        {edita && !editando && <button type="button" className="nx-botao" onClick={() => setEditando(true)}>Editar</button>}
      </header>
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      {editando ? (
        <Editor empresa={r.empresa} inicial={r.definicao} existente aoCancelar={() => setEditando(false)}
          aoSalvar={async (d, m) => { await api(`/v1/regras/${r.id}`, { metodo: "PUT", corpo: { versao: r.versao, motivo: m, definicao: d } }); setEditando(false); setAviso("Nova versão salva."); carregar(); }} />
      ) : (
        <>
          <p className="previa-regra"><b>O que faz:</b> {previa(r.definicao)}</p>
          <p className="nx-suave">Versão {r.versao} · {r.disparos_7d !== undefined ? `${r.disparos_7d} disparo(s) em 7 dias · ` : ""}atualizada {quando(r.atualizado_em)}</p>
          <div className="acoes-conta">
            <button type="button" className="nx-botao" onClick={() => void simular()}>Simular nas últimas 24 h</button>
            {edita && <>
              <div className="nx-campo"><label htmlFor="rd-motivo">Motivo</label>
                <input id="rd-motivo" maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></div>
              {r.ativa ? <button type="button" className="nx-botao" onClick={() => void situacao("desativar")}>Desativar</button>
                : <button type="button" className="nx-botao nx-botao-principal" onClick={() => void situacao("ativar")}>Ativar</button>}
            </>}
          </div>
          {simulacao && <ResultadoSimulacao s={simulacao} />}
        </>
      )}
      <h3>Versões</h3>
      <ul className="auditoria">
        {versoes.map((v) => (
          <li key={v.versao}><span className="nx-valor">v{v.versao}</span> · {quando(v.criado_em)} · <small className="nx-suave">por {v.autor}{v.motivo ? ` — ${v.motivo}` : ""}</small>
            {v.versao > 1 && <> · <button type="button" className="nx-link" onClick={() => setComparar([v.versao - 1, v.versao])}>comparar com v{v.versao - 1}</button></>}</li>
        ))}
      </ul>
      {comparar && (
        <div className="nx-cartao aviso-admin" aria-label="Comparação de versões">
          <b>v{comparar[0]} → v{comparar[1]}</b>
          <ul>{diff.map(([campo, de, para]) => <li key={campo}><span className="nx-valor">{campo}</span>: {JSON.stringify(de)} → {JSON.stringify(para)}</li>)}
            {!diff.length && <li className="nx-suave">sem diferença de conteúdo</li>}</ul>
        </div>
      )}
    </section>
  );
}
